import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { NavOverlay } from '@/components/NavOverlay';
import { SplitGallery } from '@/components/SplitGallery';
import { VerticalGallery } from '@/components/VerticalGallery';
import { HorizontalGallery } from '@/components/HorizontalGallery';
import { LoadingSpinner } from '@/components/LoadingSpinner';
import { CriaaBadge } from '@/components/CriaaBadge';
import SiteFooterLinks from '@/components/site/SiteFooterLinks';
import { useResolveUserId } from '@/hooks/useResolveUserId';
import { useTrackPageView } from '@/hooks/useTrackPageView';
import { usePageTitle } from '@/hooks/usePageTitle';
import NotFound from './NotFound';
import type { ImagePair } from '@/data/galleryData';

interface FooterLink {
  label: string;
  url: string;
}

type LayoutType = 'A' | 'B' | 'C' | 'D';

function normalizeLayout(layout: string | null | undefined): LayoutType {
  if (layout === 'B' || layout === 'C' || layout === 'D') return layout;
  return 'A';
}

export default function MenuGalleryPublic() {
  const { gallerySlug } = useParams<{ gallerySlug: string }>();
  const { userId } = useResolveUserId();
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [pairs, setPairs] = useState<ImagePair[]>([]);
  const [layout, setLayout] = useState<LayoutType>('A');
  const [showBadge, setShowBadge] = useState(true);
  const [galleryLabel, setGalleryLabel] = useState<string | null>(null);
  const [footerLinks, setFooterLinks] = useState<FooterLink[]>([]);
  const [textColor, setTextColor] = useState<string>('#000000');
  const [fontFamily, setFontFamily] = useState<string>('IBM Plex Mono');


  usePageTitle(galleryLabel);

  useTrackPageView(userId, `/g/${gallerySlug}`);

  useEffect(() => {
    if (!userId || !gallerySlug) {
      if (!gallerySlug) { setNotFound(true); setLoading(false); }
      return;
    }

    const load = async () => {
      // Find the menu item by section_slug (which stores the gallery slug)
      const { data: menuItem } = await supabase
        .from('menu_items')
        .select('id, label, layout_type, published')
        .eq('user_id', userId)
        .eq('section_slug', gallerySlug)
        .eq('menu_item_type', 'gallery')
        .maybeSingle();

      if (!menuItem) {
        setNotFound(true);
        setLoading(false);
        return;
      }

      setLayout(normalizeLayout((menuItem as any).layout_type));
      setGalleryLabel((menuItem as any).label || null);

      // Load gallery images
      const { data: images } = await supabase
        .from('menu_gallery_images')
        .select('*')
        .eq('menu_item_id', (menuItem as any).id)
        .order('sort_order');

      const imgPairs: ImagePair[] = ((images || []) as any[]).map(img => ({
        left: img.left_url,
        right: img.right_url,
        title: img.title || undefined,
        video_url: img.video_url || undefined,
        video_display_mode: img.video_display_mode || undefined,
      }));

      setPairs(imgPairs);

      // Get badge and footer settings
      const { data: settings } = await supabase
        .from('site_settings')
        .select('show_badge, footer_links, text_color, font_family')
        .eq('user_id', userId)
        .limit(1)
        .maybeSingle();
      if (settings) {
        setShowBadge((settings as any).show_badge ?? true);
        setTextColor((settings as any).text_color || '#000000');
        setFontFamily((settings as any).font_family || 'IBM Plex Mono');
        const rawLinks = (settings as any).footer_links;
        setFooterLinks(Array.isArray(rawLinks) ? rawLinks : []);
      }


      setLoading(false);
    };
    load();
  }, [userId, gallerySlug]);

  if (notFound) return <NotFound />;

  const renderGallery = () => {
    if (pairs.length === 0) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-background">
          <span style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 13, opacity: 0.4 }}>Nenhuma imagem.</span>
        </div>
      );
    }
    switch (layout) {
      case 'B': return <VerticalGallery pairs={pairs} />;
      case 'C': return <HorizontalGallery pairs={pairs} />;
      case 'D': return <SplitGallery pairs={pairs} layout="horizontal" />;
      default: return <SplitGallery pairs={pairs} />;
    }
  };

  return (
    <>
      <LoadingSpinner visible={loading} />
      <NavOverlay />
      {!loading && renderGallery()}
      {!loading && (
        <SiteFooterLinks
          links={footerLinks}
          textColor={textColor}
          fontFamily={fontFamily}
        />
      )}
      {!loading && <CriaaBadge userId={userId} showBadge={showBadge} />}
    </>
  );

}
